import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { 
  Trophy, 
  TrendingUp, 
  TrendingDown,
  Medal,
  Users,
  Crown,
  PieChart,
  Eye,
  Lock,
  Wallet,
  Sparkles
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import AppLayout from '../components/AppLayout';

const LeaderboardPage = () => {
  const navigate = useNavigate();
  const { api } = useAuth();
  const [leaderboard, setLeaderboard] = useState([]);
  const [totalTraders, setTotalTraders] = useState(0);
  const [isProView, setIsProView] = useState(false);
  const [tierFilter, setTierFilter] = useState('free');
  const [loading, setLoading] = useState(true);
  const [selectedTrader, setSelectedTrader] = useState(null);

  const fetchLeaderboard = useCallback(async (filter) => {
    setLoading(true);
    try {
      const response = await api.get(`/leaderboard?tier_filter=${filter}`);
      setLeaderboard(response.data.leaderboard);
      setTotalTraders(response.data.total_traders);
      setIsProView(response.data.is_pro_view);
    } catch (error) {
      console.error('Failed to fetch leaderboard:', error);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchLeaderboard(tierFilter);
  }, [fetchLeaderboard, tierFilter]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const formatPercent = (value) => {
    const prefix = value >= 0 ? '+' : '';
    return `${prefix}${value.toFixed(2)}%`;
  };

  const getRankIcon = (rank) => {
    switch (rank) {
      case 1:
        return <Crown className="w-6 h-6 text-yellow-500" />;
      case 2:
        return <Medal className="w-6 h-6 text-gray-400" />;
      case 3:
        return <Medal className="w-6 h-6 text-amber-600" />;
      default:
        return <span className="w-6 h-6 flex items-center justify-center text-gray-500 font-bold">{rank}</span>;
    }
  };

  const getRankStyle = (rank) => {
    switch (rank) {
      case 1:
        return 'bg-gradient-to-r from-yellow-50 to-amber-50 border-yellow-200';
      case 2:
        return 'bg-gradient-to-r from-gray-50 to-slate-50 border-gray-200';
      case 3:
        return 'bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200';
      default:
        return 'bg-white border-stone-100';
    }
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-[60vh]">
          <div className="animate-pulse-subtle text-primary font-serif text-xl">Loading leaderboard...</div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="space-y-8" data-testid="leaderboard-page">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="font-serif text-3xl font-semibold text-[#1F2937] flex items-center gap-3">
              <Trophy className="w-8 h-8 text-yellow-500" />
              Leaderboard
            </h1>
            <p className="text-gray-500 mt-1">Top 12 {tierFilter === 'paid' ? 'Pro' : 'Free'} traders</p>
          </div>
          <div className="flex items-center gap-4">
            {/* Tier Toggle */}
            <Tabs value={tierFilter} onValueChange={setTierFilter} className="bg-stone-100 rounded-lg p-1">
              <TabsList className="bg-transparent">
                <TabsTrigger 
                  value="free" 
                  className="data-[state=active]:bg-white data-[state=active]:shadow-sm"
                  data-testid="filter-free"
                >
                  <Users className="w-4 h-4 mr-2" />
                  Free Users
                </TabsTrigger>
                <TabsTrigger 
                  value="paid" 
                  className="data-[state=active]:bg-white data-[state=active]:shadow-sm"
                  data-testid="filter-pro"
                >
                  <Sparkles className="w-4 h-4 mr-2" />
                  Pro Users
                </TabsTrigger>
              </TabsList>
            </Tabs>
            
            {isProView && (
              <Badge className="bg-[#1A4D2E] text-white">
                <Eye className="w-3 h-3 mr-1" />
                Pro View
              </Badge>
            )}
          </div>
        </div>

        {/* Pro Feature Notice for Free Users */}
        {!isProView && (
          <Card className="bg-gradient-to-r from-[#1A4D2E] to-[#2D5A3D] text-white border-0">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center">
                    <PieChart className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg">Unlock Portfolio Insights</h3>
                    <p className="text-white/80 text-sm">Pro users can see how top traders allocate their portfolios</p>
                  </div>
                </div>
                <Button 
                  className="bg-[#C1F23D] text-[#1A4D2E] hover:bg-[#b2e030]"
                  onClick={() => navigate('/settings')}
                >
                  Upgrade to Pro
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Top 3 Podium */}
        {leaderboard.length >= 3 && (
          <div className="grid grid-cols-3 gap-4">
            {/* 2nd Place */}
            <Card className="bg-gradient-to-b from-gray-50 to-white border-gray-200 mt-8">
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Medal className="w-8 h-8 text-gray-400" />
                </div>
                <p className="text-2xl font-bold text-gray-400">2nd</p>
                <p className="font-semibold text-[#1F2937] mt-2">{leaderboard[1].anonymous_name}</p>
                <p className={`text-lg font-bold mt-1 ${leaderboard[1].total_return >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                  {formatPercent(leaderboard[1].total_return)}
                </p>
                <p className="text-sm text-gray-500">{formatCurrency(leaderboard[1].total_equity)}</p>
                {leaderboard[1].starting_cash > 10000 && (
                  <Badge variant="outline" className="mt-2 text-xs">
                    Started: {formatCurrency(leaderboard[1].starting_cash)}
                  </Badge>
                )}
                {isProView && leaderboard[1].holdings?.length > 0 && (
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="mt-2 text-xs"
                    onClick={() => setSelectedTrader(leaderboard[1])}
                  >
                    <Eye className="w-3 h-3 mr-1" /> View Holdings
                  </Button>
                )}
              </CardContent>
            </Card>

            {/* 1st Place */}
            <Card className="bg-gradient-to-b from-yellow-50 to-white border-yellow-200 shadow-lg">
              <CardContent className="p-6 text-center">
                <div className="w-20 h-20 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Crown className="w-10 h-10 text-yellow-500" />
                </div>
                <p className="text-3xl font-bold text-yellow-500">1st</p>
                <p className="font-semibold text-[#1F2937] mt-2 text-lg">{leaderboard[0].anonymous_name}</p>
                <p className={`text-xl font-bold mt-1 ${leaderboard[0].total_return >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                  {formatPercent(leaderboard[0].total_return)}
                </p>
                <p className="text-sm text-gray-500">{formatCurrency(leaderboard[0].total_equity)}</p>
                {leaderboard[0].starting_cash > 10000 && (
                  <Badge variant="outline" className="mt-2 text-xs">
                    Started: {formatCurrency(leaderboard[0].starting_cash)}
                  </Badge>
                )}
                {isProView && leaderboard[0].holdings?.length > 0 && (
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="mt-2 text-xs"
                    onClick={() => setSelectedTrader(leaderboard[0])}
                  >
                    <Eye className="w-3 h-3 mr-1" /> View Holdings
                  </Button>
                )}
              </CardContent>
            </Card>

            {/* 3rd Place */}
            <Card className="bg-gradient-to-b from-amber-50 to-white border-amber-200 mt-8">
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Medal className="w-8 h-8 text-amber-600" />
                </div>
                <p className="text-2xl font-bold text-amber-600">3rd</p>
                <p className="font-semibold text-[#1F2937] mt-2">{leaderboard[2].anonymous_name}</p>
                <p className={`text-lg font-bold mt-1 ${leaderboard[2].total_return >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                  {formatPercent(leaderboard[2].total_return)}
                </p>
                <p className="text-sm text-gray-500">{formatCurrency(leaderboard[2].total_equity)}</p>
                {leaderboard[2].starting_cash > 10000 && (
                  <Badge variant="outline" className="mt-2 text-xs">
                    Started: {formatCurrency(leaderboard[2].starting_cash)}
                  </Badge>
                )}
                {isProView && leaderboard[2].holdings?.length > 0 && (
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="mt-2 text-xs"
                    onClick={() => setSelectedTrader(leaderboard[2])}
                  >
                    <Eye className="w-3 h-3 mr-1" /> View Holdings
                  </Button>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Full Rankings Table */}
        <Card className="bg-white border-stone-100 shadow-card">
          <CardHeader>
            <CardTitle className="font-serif text-xl text-[#1F2937]">
              Top 12 {tierFilter === 'paid' ? 'Pro' : 'Free'} Traders
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {leaderboard.map((trader) => (
                <div
                  key={trader.rank}
                  className={`flex items-center justify-between p-4 rounded-lg border ${getRankStyle(trader.rank)} hover:shadow-md transition-shadow cursor-pointer`}
                  onClick={() => isProView && trader.holdings?.length > 0 && setSelectedTrader(trader)}
                  data-testid={`rank-${trader.rank}`}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 flex items-center justify-center">
                      {getRankIcon(trader.rank)}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-[#1F2937]">{trader.anonymous_name}</p>
                        {trader.tier === 'paid' && (
                          <Badge className="bg-[#1A4D2E] text-white text-xs">Pro</Badge>
                        )}
                        {isProView && trader.top_performer && (
                          <Badge variant="outline" className="text-xs">
                            Top: {trader.top_performer}
                          </Badge>
                        )}
                      </div>
                      <p className="text-xs text-gray-500">
                        {trader.positions_count} positions • Started: {formatCurrency(trader.starting_cash || 10000)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-6">
                    {/* Pro: Show allocation preview */}
                    {isProView && trader.holdings?.length > 0 && (
                      <div className="hidden md:flex items-center gap-1">
                        {trader.holdings.slice(0, 3).map((h, i) => (
                          <Badge key={i} variant="secondary" className="text-xs">
                            {h.symbol} {h.allocation_percent}%
                          </Badge>
                        ))}
                        {trader.holdings.length > 3 && (
                          <span className="text-xs text-gray-400">+{trader.holdings.length - 3}</span>
                        )}
                      </div>
                    )}
                    {!isProView && trader.positions_count > 0 && (
                      <div className="hidden md:flex items-center gap-1 text-gray-400">
                        <Lock className="w-3 h-3" />
                        <span className="text-xs">Pro only</span>
                      </div>
                    )}
                    <div className="text-right">
                      <p className={`font-bold text-lg ${trader.total_return >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                        {trader.total_return >= 0 ? <TrendingUp className="w-4 h-4 inline mr-1" /> : <TrendingDown className="w-4 h-4 inline mr-1" />}
                        {formatPercent(trader.total_return)}
                      </p>
                      <p className="text-sm text-gray-500 tabular-nums">{formatCurrency(trader.total_equity)}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {leaderboard.length === 0 && (
              <div className="text-center py-12">
                <Trophy className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                <p className="text-gray-500">No {tierFilter === 'paid' ? 'Pro' : 'Free'} traders yet!</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Holdings Detail Dialog (Pro Only) */}
        <Dialog open={!!selectedTrader} onOpenChange={() => setSelectedTrader(null)}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-serif text-xl flex items-center gap-2">
                <PieChart className="w-5 h-5 text-[#1A4D2E]" />
                {selectedTrader?.anonymous_name}'s Portfolio
              </DialogTitle>
            </DialogHeader>
            
            {selectedTrader && (
              <div className="space-y-6 mt-4">
                {/* Summary */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-stone-50 rounded-xl text-center">
                    <p className="text-xs uppercase tracking-wider text-gray-500">Total Return</p>
                    <p className={`text-2xl font-bold ${selectedTrader.total_return >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                      {formatPercent(selectedTrader.total_return)}
                    </p>
                  </div>
                  <div className="p-4 bg-stone-50 rounded-xl text-center">
                    <p className="text-xs uppercase tracking-wider text-gray-500">Portfolio Value</p>
                    <p className="text-2xl font-bold text-[#1F2937]">
                      {formatCurrency(selectedTrader.total_equity)}
                    </p>
                  </div>
                </div>

                {selectedTrader.starting_cash > 10000 && (
                  <div className="text-center text-sm text-gray-500">
                    Started with {formatCurrency(selectedTrader.starting_cash)}
                  </div>
                )}

                {/* Holdings Breakdown */}
                <div>
                  <h4 className="font-semibold text-[#1F2937] mb-3 flex items-center gap-2">
                    <TrendingUp className="w-4 h-4" />
                    Holdings Allocation
                  </h4>
                  <div className="space-y-2">
                    {selectedTrader.holdings?.map((holding, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-stone-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center font-bold text-[#1A4D2E] border border-stone-200">
                            {holding.symbol.slice(0, 2)}
                          </div>
                          <div>
                            <p className="font-semibold text-[#1F2937]">{holding.symbol}</p>
                            <p className="text-xs text-gray-500">{holding.allocation_percent}% of portfolio</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className={`font-bold ${holding.return_percent >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                            {formatPercent(holding.return_percent)}
                          </p>
                          <p className="text-xs text-gray-500">{formatCurrency(holding.market_value)}</p>
                        </div>
                      </div>
                    ))}
                    
                    {/* Cash Position */}
                    {selectedTrader.cash_allocation > 0 && (
                      <div className="flex items-center justify-between p-3 bg-stone-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center border border-stone-200">
                            <Wallet className="w-5 h-5 text-gray-400" />
                          </div>
                          <div>
                            <p className="font-semibold text-[#1F2937]">Cash</p>
                            <p className="text-xs text-gray-500">{selectedTrader.cash_allocation}% of portfolio</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-gray-500">-</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Disclaimer */}
                <p className="text-xs text-gray-400 text-center">
                  This is anonymous data. User identity is not disclosed.
                </p>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </AppLayout>
  );
};

export default LeaderboardPage;
